import axios from 'axios';
import type { PlayerData } from '@/vite-env.d';
import { getLocale } from '@/lib/i18n';

declare module 'axios' {
  export interface AxiosRequestConfig {
    skipAuthRefresh?: boolean;
    __authRetried?: boolean;
  }
}

const JWT_KEY = 'vsro_jwt_token';
const JWT_EXPIRY_KEY = 'vsro_jwt_expiry';

export interface RefreshedSession {
  token: string;
  expiresIn: number;
  user: { jid: number; charId: number };
  player: PlayerData | null;
}

interface ValidateSessionApiResponse {
  status: string;
  message?: string;
  data?: {
    token: string;
    expiresIn: number;
    user: { jid: number; charId: number };
    player: PlayerData | null;
    fromCache?: boolean;
  };
}

/** Dominio API (sin / final). Igual que vsro.me: baseURL = host, rutas /api/v1/... */
export const apiUrl = (import.meta.env.VITE_API_URL?.trim() || '').replace(/\/$/, '');

export const api = axios.create({
  baseURL: apiUrl,
  timeout: 15000,
});

export function getCachedToken(): string | null {
  try {
    const token = localStorage.getItem(JWT_KEY);
    const expiry = localStorage.getItem(JWT_EXPIRY_KEY);
    if (!token || !expiry) return null;

    if (Date.now() > parseInt(expiry, 10)) {
      localStorage.removeItem(JWT_KEY);
      localStorage.removeItem(JWT_EXPIRY_KEY);
      return null;
    }

    if (!decodeJwtPayload(token)) {
      localStorage.removeItem(JWT_KEY);
      localStorage.removeItem(JWT_EXPIRY_KEY);
      return null;
    }

    return token;
  } catch {
    return null;
  }
}

export function cacheToken(token: string, expiresInSeconds: number): void {
  const expiryTime = Date.now() + expiresInSeconds * 1000;
  localStorage.setItem(JWT_KEY, token);
  localStorage.setItem(JWT_EXPIRY_KEY, expiryTime.toString());
  if (typeof window !== 'undefined') {
    window.__APPWEB_JWT__ = token;
  }
}

export function clearCachedToken(): void {
  try {
    localStorage.removeItem(JWT_KEY);
    localStorage.removeItem(JWT_EXPIRY_KEY);
  } catch {
    /* ignore */
  }
  if (typeof window !== 'undefined') {
    delete window.__APPWEB_JWT__;
  }
}

export function getAuthToken(): string | null {
  return getCachedToken();
}

/** @deprecated alias — JWT en localStorage (paridad vsro.me) */
export function getStoredJwt(): string | null {
  return getCachedToken();
}

export function decodeJwtPayload(
  token: string,
): { jid: number; charId: number; hwidHash: string } | null {
  try {
    const base64Payload = token.split('.')[1];
    if (!base64Payload) return null;
    const payload = JSON.parse(atob(base64Payload)) as {
      jid?: number;
      charId?: number;
      hwidHash?: string;
    };
    const jid = Number(payload.jid || 0);
    const charId = Number(payload.charId || 0);
    const hwidHash = typeof payload.hwidHash === 'string' ? payload.hwidHash : '';
    if (!jid || !charId || hwidHash.length !== 64) return null;
    return { jid, charId, hwidHash };
  } catch {
    return null;
  }
}

/** charId canónico desde JWT cacheado — usar en hooks cuando no hay user del contexto */
export function resolveCharIdFromJwt(): number {
  const jwt = getCachedToken();
  if (!jwt) return 0;
  return decodeJwtPayload(jwt)?.charId ?? 0;
}

/** Headers para axios, fetch legacy (itemTooltip) y WebView2 */
export function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const jwt = getCachedToken();
  if (jwt) headers.Authorization = `Bearer ${jwt}`;
  return headers;
}

api.interceptors.request.use((config) => {
  const isValidateSession = config.url?.includes('/auth/validate-session');
  if (!isValidateSession) {
    const jwt = getCachedToken();
    if (jwt) {
      config.headers.Authorization = `Bearer ${jwt}`;
    }
  }
  config.headers['Accept-Language'] = getLocale();
  return config;
});

/**
 * Renueva JWT vía validate-session — paridad vsro.me.
 * El C# inyecta X-Session-Token en requests a api.*; el JS no envía el WebToken.
 */
export async function refreshSessionFromToken(): Promise<RefreshedSession | null> {
  try {
    const { data } = await api.get<ValidateSessionApiResponse>('/api/v1/auth/validate-session', {
      skipAuthRefresh: true,
    });

    if (data.status === 'success' && data.data?.token) {
      cacheToken(data.data.token, data.data.expiresIn);
      installWebViewAuthBridge();
      return data.data;
    }
  } catch {
    /* validate-session failed */
  }

  return null;
}

api.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (!axios.isAxiosError(error) || !error.config) throw error;

    const config = error.config as typeof error.config & {
      skipAuthRefresh?: boolean;
      __authRetried?: boolean;
    };

    if (
      error.response?.status !== 401 ||
      config.skipAuthRefresh ||
      config.__authRetried
    ) {
      throw error;
    }

    config.__authRetried = true;
    clearCachedToken();

    const session = await refreshSessionFromToken();
    if (!session) throw error;

    return api.request(config);
  },
);

export const devAuthBypass =
  import.meta.env.DEV && import.meta.env.VITE_ALLOW_DEV_AUTH_BYPASS === 'true';

/** Registro global para itemTooltip.js y scripts legacy en WebView2 */
export function installWebViewAuthBridge(): void {
  window.__APPWEB_API_BASE__ = apiUrl ? `${apiUrl}/api/v1` : '/api/v1';
  window.__APPWEB_GET_AUTH_HEADERS__ = getAuthHeaders;
  const jwt = getCachedToken();
  if (jwt) window.__APPWEB_JWT__ = jwt;
}
